/**
 * @fileoverview Local-first service for the schedule-rule catalog
 *   (issue #737 Phase 6, plan §11.5 step 58).
 * @module system-web/services/scheduleRuleCatalogService
 *
 * Responsibilities:
 * - Read the `systemScheduleRuleCatalog` from Dexie
 *   (`system_schedule_rule_catalog` — synced pull-only mirror).
 * - Route writes through system-api (`apiFetch`); the API propagates the
 *   catalog to every tenant's read-only `scheduleRuleCatalog` collection.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb)
 * - ../apiClient (dynamic import for writes — online mutations)
 *
 * Architecture:
 * - Same server-authoritative contract as systemInspectionProgramService:
 *   Dexie for reads, apiClient for writes, response put back into Dexie.
 *
 * Notes:
 * - `ruleTypeCode` is immutable after creation — tenant rules reference it;
 *   the update path never sends it.
 *
 * @localFirst
 * Source of Truth: hybrid — Dexie for reads, system-api for writes.
 * Write Flow: apiClient -> Dexie put; sync pull reconciles eventually.
 * Sync Strategy: read-only pull.
 * Conflict Resolution: server-authoritative.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Contracts — mirror api/packages/db/src/inspectionProgramReplay.ts
// ---------------------------------------------------------------------------

export interface RuleCatalogOption {
  code: string;
  name: string;
}

/**
 * @contract ScheduleRuleCatalogEntry
 * Purpose: One rule-type entry — describes the rule family and the option
 *          menus (conditions/actions/modes/resolutions) tenant rule forms
 *          may offer.
 * Fields:  ruleTypeCode (stable, immutable), name, description, option
 *          menus, configurableFields, active.
 * Rules:   ruleTypeCode unique + immutable; `active:false` propagates
 *          catalog deactivation to tenants.
 * Sync:    Server-authoritative; pull-only Dexie mirror.
 */
export interface ScheduleRuleCatalogEntry {
  _id: string;
  ruleTypeCode: string;
  name: string;
  description?: string;
  conditions?: RuleCatalogOption[];
  actions?: RuleCatalogOption[];
  modes?: RuleCatalogOption[];
  conflictResolutions?: RuleCatalogOption[];
  configurableFields?: Record<string, unknown>;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  _deleted?: boolean;
}

export interface ScheduleRuleCatalogPatch {
  name?: string;
  description?: string;
  conditions?: RuleCatalogOption[];
  actions?: RuleCatalogOption[];
  modes?: RuleCatalogOption[];
  conflictResolutions?: RuleCatalogOption[];
  configurableFields?: Record<string, unknown>;
  active?: boolean;
}

// ---------------------------------------------------------------------------
// Dexie reads
// ---------------------------------------------------------------------------

export async function listScheduleRuleCatalog(): Promise<ScheduleRuleCatalogEntry[]> {
  const db = await getSystemDb();
  const rows = (await db.system_schedule_rule_catalog.toArray()) as unknown as ScheduleRuleCatalogEntry[];
  return rows
    .filter((e) => !e._deleted)
    .sort((a, b) => (a.ruleTypeCode ?? '').localeCompare(b.ruleTypeCode ?? ''));
}

// ---------------------------------------------------------------------------
// Writes — apiClient -> system-api -> Dexie put
// ---------------------------------------------------------------------------

export async function createScheduleRuleCatalogEntry(
  input: { ruleTypeCode: string; name: string } & ScheduleRuleCatalogPatch,
): Promise<ScheduleRuleCatalogEntry> {
  const { api } = await import('../apiClient');
  const result = (await api.scheduleRuleCatalog.create(input)) as {
    entry: ScheduleRuleCatalogEntry;
    propagation?: unknown;
  };
  const db = await getSystemDb();
  await db.system_schedule_rule_catalog.put(result.entry as unknown as Record<string, unknown>);
  wakeLocalWrite('system_schedule_rule_catalog');
  return result.entry;
}

export async function updateScheduleRuleCatalogEntry(
  id: string,
  patch: ScheduleRuleCatalogPatch,
): Promise<ScheduleRuleCatalogEntry> {
  const { api } = await import('../apiClient');
  const result = (await api.scheduleRuleCatalog.update(id, patch)) as {
    entry: ScheduleRuleCatalogEntry;
    propagation?: unknown;
  };
  const db = await getSystemDb();
  await db.system_schedule_rule_catalog.put(result.entry as unknown as Record<string, unknown>);
  wakeLocalWrite('system_schedule_rule_catalog');
  return result.entry;
}
